import type { ApproverMode } from 'models/policies/policy';
import { SkillPreflight } from 'models/capabilities/capability';
import { DocumentActingIdentity } from 'models/integrations/documentConnection';
import { DocumentProviderType } from 'models/integrations/documentProvider';

//
// One user request and its run (the API's Execution). Follow-up requests in the same conversation are new
// executions with the same conversationId. POST executions writes Queued and the API's worker moves it to Running;
// Running pauses as WaitingForApproval (resumed through Queued) and ends in Completed, Failed or Canceled.
//
export enum ExecutionStatus {
  Running = 0,
  Completed = 1,
  Failed = 2,
  Canceled = 3,
  // paused at a tool call until its approval is decided or expires
  WaitingForApproval = 4,
  // accepted and waiting for the API's worker (a new run, or a run resumed after an approval)
  Queued = 5,
}

// Queued or Running: the execution has not ended and is not paused
export const isExecutionInProgress = (status: ExecutionStatus): boolean =>
  status === ExecutionStatus.Queued || status === ExecutionStatus.Running;

// Queued, Running or WaitingForApproval: the execution has not ended, so its documents cannot be saved yet (EXEC0008)
export const isExecutionUnfinished = (status: ExecutionStatus): boolean =>
  isExecutionInProgress(status) || status === ExecutionStatus.WaitingForApproval;

// What happened at one point of an execution: the timeline of spec section 21.1
export enum ExecutionStepKind {
  Started = 0,
  ModelSelected = 1,
  DocumentCreated = 2,
  Completed = 3,
  Failed = 4,
  Canceled = 5,
  DocumentSaved = 6,
  // the first step of an execution bound to a Skill version
  SkillMatched = 7,
  // the first step of an execution without a Skill
  AdHoc = 8,
  // a tool call the tool gate allowed and ran (its outcome Ok or Error)
  ToolCallCompleted = 9,
  // a tool call the tool gate refused before it ran
  ToolCallRefused = 10,
  // a tool call paused the run until a human approves it
  ApprovalRequested = 11,
  // the approval was granted; the run resumes
  ApprovalGranted = 12,
  // the approval was rejected; the run ends Failed
  ApprovalRejected = 13,
  // the approval expired undecided; the run ends Failed
  ApprovalExpired = 14,
  // a file the model produced was left out (phase 57); the text is its media type, the payload the reason
  OutputDropped = 15,
  // the model ran code in the provider's sandbox (phase 59); the payload holds the start of what it printed
  CodeExecuted = 16,
}

// Why a file the model produced was left out (the API's OutputDropped reasons, phase 57)
export type OutputDroppedReason = 'notAllowed' | 'tooLarge' | 'runLimit' | 'notMatching' | 'notFetched';

// The payload of a CodeExecuted step (phase 59): the start of the output (at most 2000 characters) and whether the code
// failed
export class ExecutionCodeExecutedStep {
  callId: string;

  stdout?: string;

  stdoutTruncated: boolean;

  failed: boolean;

  errorCode?: string;

  constructor() {
    this.callId = '';
    this.stdoutTruncated = false;
    this.failed = false;
  }
}

// The payload of an OutputDropped step: the media type, the reason and the size when known
export class ExecutionOutputDroppedStep {
  mediaType: string;

  reason: string;

  sizeBytes?: number;

  constructor() {
    this.mediaType = '';
    this.reason = '';
  }
}

// How a tool call ended (the API's ToolCallOutcome)
export enum ToolCallOutcome {
  Ok = 0,
  Refused = 1,
  Error = 2,
  // the call did not run: the run paused for a human approval
  AwaitingApproval = 3,
}

// Whose credential a tool call ran with (the API's ToolCallIdentityMode): the user's own, an organization credential
// (D5), or none (a built-in tool, or a refused call)
export enum ToolCallIdentityMode {
  User = 0,
  Shared = 1,
  None = 2,
}

// Whether a tool call reads or changes data in the external system
export enum ToolCallAccess {
  Read = 0,
  Write = 1,
}

// A tool call as its ToolCallCompleted, ToolCallRefused or ApprovalRequested step records it; the arguments are on
// the ToolCall row
export class ExecutionToolCallStep {
  toolCallId: string;

  toolName: string;

  // undefined for a built-in tool (create_document, read_skill_file)
  capabilityId?: string;

  capabilityKey?: string;

  outcome: ToolCallOutcome;

  // the CAP code the model also saw; set when the call was refused
  refusalCode?: string;

  durationMs: number;

  identityMode: ToolCallIdentityMode;

  // undefined when the gate refused the call before it knew
  access?: ToolCallAccess;

  // the MCP tool, or "METHOD /pattern" of an HTTPS capability; undefined for a built-in tool
  operation?: string;

  // the approval the call waited for (AwaitingApproval) or ran under (a call resumed after the approval)
  approvalRequestId?: string;

  // a call resumed after its approval: the AwaitingApproval call it continues
  resumedFromToolCallId?: string;

  // run_skill (phase 10): the child execution it queued, its status when the call answered, the approval a paused
  // child waits for, and the Skill it ran
  childExecutionId?: string;

  childStatus?: ExecutionStatus;

  childApprovalRequestId?: string;

  childSkillName?: string;

  // the parent stopped while it waited for the child; the child continues
  isParentStopped?: boolean;

  constructor() {
    this.toolCallId = '';
    this.toolName = '';
    this.outcome = ToolCallOutcome.Ok;
    this.durationMs = 0;
    this.identityMode = ToolCallIdentityMode.None;
  }
}

// The pause of an ApprovalRequested step: which request, why, and who was asked
export class ExecutionApprovalRequestStep {
  approvalRequestId: string;

  // the rule that required the approval: a policy id, "skill:{versionId}:approvals[n]", "default-write-approval"
  ruleId?: string;

  policyId?: string;

  // every rule that fired, in business words with the evaluated values
  reason?: string;

  approverMode?: ApproverMode;

  approverGroupId?: string;

  approverGroupName?: string;

  // how many people were asked
  approverCount: number;

  expiresAt?: Date;

  constructor() {
    this.approvalRequestId = '';
    this.approverCount = 0;
  }
}

// The end of a pause as its ApprovalGranted, ApprovalRejected or ApprovalExpired step records it
export class ExecutionApprovalDecisionStep {
  approvalRequestId: string;

  toolCallId?: string;

  // the decider's name; undefined for an expiry
  deciderName?: string;

  // an automated decider, not a person
  isSystem: boolean;

  comment?: string;

  decidedAt?: Date;

  // ApprovalExpired: when the request would have expired and when the sweep ended it
  expiresAt?: Date;

  expiredAt?: Date;

  constructor() {
    this.approvalRequestId = '';
    this.isSystem = false;
  }
}

// One tool call of an execution as the tool gate decided and ran it (GET executions/{id}/toolcalls)
export class ToolCall {
  toolCallId: string;

  executionId: string;

  // undefined for a built-in tool
  capabilityId?: string;

  toolName: string;

  // the arguments as the model sent them (JSON); undefined when they were larger than 64 KB
  argumentsJson?: string;

  outcome: ToolCallOutcome;

  refusalCode?: string;

  durationMs: number;

  identityMode: ToolCallIdentityMode;

  startedAt: Date;

  endedAt: Date;

  // the approval this call waited for (AwaitingApproval) or ran under (a resumed call)
  approvalRequestId?: string;

  // a resumed call: the AwaitingApproval call it continues
  resumedFromToolCallId?: string;

  constructor() {
    this.toolCallId = '';
    this.executionId = '';
    this.toolName = '';
    this.outcome = ToolCallOutcome.Ok;
    this.durationMs = 0;
    this.identityMode = ToolCallIdentityMode.None;
    this.startedAt = new Date();
    this.endedAt = new Date();
  }
}

// A produced document saved to a document provider: the answer of the save and the payload of a DocumentSaved step
export class ExecutionDocumentSaveResult {
  // set on a DocumentSaved step; the save answer is for the document that was saved
  documentId?: string;

  // the link that opens the file in the provider's own UI; undefined when the provider gave none
  webUrl?: string;

  itemId: string;

  // the file name at the provider, which renames on a name clash
  fileName: string;

  providerType: DocumentProviderType;

  connectionId: number;

  connectionName: string;

  actingIdentity: DocumentActingIdentity;

  constructor() {
    this.itemId = '';
    this.fileName = '';
    this.providerType = DocumentProviderType.SharePoint;
    this.connectionId = 0;
    this.connectionName = '';
    this.actingIdentity = DocumentActingIdentity.User;
  }
}

// The Skill version an execution ran under, as its SkillMatched step records it at run time
export class ExecutionSkillMatch {
  skillId: string;

  skillVersionId: string;

  skillKey: string;

  skillName: string;

  version: string;

  // the router's sentence the client sent with the request
  explanation?: string;

  // the name of the user who approved the version
  approvedBy?: string;

  approvedAt?: Date;

  // the time limit of the run: the Skill's max_execution_minutes capped by the platform limit
  runLimitSeconds?: number;

  constructor() {
    this.skillId = '';
    this.skillVersionId = '';
    this.skillKey = '';
    this.skillName = '';
    this.version = '';
  }
}

export class ExecutionStep {
  stepNo: number;

  kind: ExecutionStepKind;

  // the variable part of the step: a model name, a document title, an error code, a saved file name
  text: string;

  occurredAt: Date;

  // DocumentSaved: where the file went and as whom
  savedDocument?: ExecutionDocumentSaveResult;

  // SkillMatched: the Skill version, the router's explanation, the approver and the run limit
  skillMatch?: ExecutionSkillMatch;

  // ToolCallCompleted, ToolCallRefused: the tool, the capability, the outcome and the identity; ApprovalRequested:
  // the paused call with the outcome AwaitingApproval
  toolCall?: ExecutionToolCallStep;

  // ApprovalRequested: the request, the rule and the approvers
  approvalRequest?: ExecutionApprovalRequestStep;

  // ApprovalGranted, ApprovalRejected, ApprovalExpired: the decider and the comment, or the expiry
  approvalDecision?: ExecutionApprovalDecisionStep;

  // OutputDropped: why the model's file was left out
  outputDropped?: ExecutionOutputDroppedStep;

  // CodeExecuted: what the model's code printed and whether it failed
  codeExecuted?: ExecutionCodeExecutedStep;

  constructor() {
    this.stepNo = 0;
    this.kind = ExecutionStepKind.Started;
    this.text = '';
    this.occurredAt = new Date();
  }
}

// The content type of an Excel workbook an execution produced (phase 56: its markdown tables as sheets)
export const SpreadsheetContentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export function isSpreadsheetDocument(contentType: string | undefined): boolean {
  return contentType === SpreadsheetContentType;
}

// A document an execution produced: the markdown for the preview and the file the download returns (a .docx, or an
// .xlsx since phase 56)
export class ExecutionDocument {
  documentId: string;

  title: string;

  fileName: string;

  contentType: string;

  sizeBytes: number;

  markdown: string;

  createdAt: Date;

  constructor() {
    this.documentId = '';
    this.title = '';
    this.fileName = '';
    this.contentType = '';
    this.sizeBytes = 0;
    this.markdown = '';
    this.createdAt = new Date();
  }
}

// One row of the history list
export class ExecutionSummary {
  executionId: string;

  conversationId: string;

  status: ExecutionStatus;

  title: string;

  modelId: string;

  startedAt: Date;

  endedAt?: Date;

  // the code a failed execution ended with; exceptions.json translates it
  errorCode: string;

  // the Skill version the execution ran under; undefined for an execution without a Skill
  skillId?: string;

  skillVersionId?: string;

  // the Skill's current name and the bound version as semver text (1.2.0)
  skillName?: string;

  skillVersion?: string;

  // the current name of the schedule that queued the execution; undefined for an execution a person started and once
  // the schedule is deleted
  skillScheduleName?: string;

  // the ad hoc execution whose run_skill call queued this one (phase 10); undefined otherwise
  parentExecutionId?: string;

  constructor() {
    this.executionId = '';
    this.conversationId = '';
    this.status = ExecutionStatus.Running;
    this.title = '';
    this.modelId = '';
    this.startedAt = new Date();
    this.endedAt = undefined;
    this.errorCode = '';
  }
}

export default class Execution extends ExecutionSummary {
  request: string;

  answer: string;

  modelProvider: string;

  inputTokens?: number;

  outputTokens?: number;

  steps: ExecutionStep[];

  documents: ExecutionDocument[];

  // a test case or an owner's simulation: never in the history, opened from its test result
  isSimulation: boolean;

  // the Skill test run of a simulated execution
  skillTestRunId?: string;

  // the schedule whose occurrence queued the execution; undefined for an execution a person started
  skillScheduleId?: string;

  // the user who ran the execution (phase 63): an administrator opens any run of the organization and the detail names
  // its owner; userName is undefined when the user is no longer known
  userId: string;

  userName?: string;

  constructor() {
    super();
    this.isSimulation = false;
    this.userId = '';
    this.userName = undefined;
    this.request = '';
    this.answer = '';
    this.modelProvider = '';
    this.inputTokens = undefined;
    this.outputTokens = undefined;
    this.steps = [];
    this.documents = [];
  }
}

// The answer of Clear all (phase 63): how many finished runs of the user were removed and how many unfinished ones stay
export class ExecutionClearResult {
  deleted: number;

  keptRunning: number;

  constructor() {
    this.deleted = 0;
    this.keptRunning = 0;
  }
}

// POST executions/route: the published Skill a request is meant for, or no match. adHocAllowed is the organization's
// setting at the time of the route; the settings loaded at sign-in can be older.
export class ExecutionRoute {
  // a match with both its Skill id and its version id; only such a route shows the confirmation card
  matched: boolean;

  // the router reported a match without its Skill id or version id: nothing runs, the no-route text shows
  isIncompleteMatch: boolean;

  skillId?: string;

  skillVersionId?: string;

  skillKey: string;

  skillName: string;

  // the published version as semver text (1.2.0)
  version: string;

  // the router's sentence for the user; also set on a no match when the model gave one
  explanation: string;

  // the name of the user who approved the version
  approvedBy?: string;

  approvedAt?: Date;

  adHocAllowed: boolean;

  // on a match: the signed-in user's status per capability the version requires (spec 16.4)
  preflight?: SkillPreflight;

  constructor() {
    this.matched = false;
    this.isIncompleteMatch = false;
    this.skillKey = '';
    this.skillName = '';
    this.version = '';
    this.explanation = '';
    this.adHocAllowed = true;
  }

  clone(): ExecutionRoute {
    const clone = new ExecutionRoute();
    clone.matched = this.matched;
    clone.isIncompleteMatch = this.isIncompleteMatch;
    clone.skillId = this.skillId;
    clone.skillVersionId = this.skillVersionId;
    clone.skillKey = this.skillKey;
    clone.skillName = this.skillName;
    clone.version = this.version;
    clone.explanation = this.explanation;
    clone.approvedBy = this.approvedBy;
    clone.approvedAt = this.approvedAt;
    clone.adHocAllowed = this.adHocAllowed;
    clone.preflight = this.preflight;

    return clone;
  }
}
